import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import './AdminJobList.css';
import JobPostingPreview from '../Recruiter/JobPosting/JobPostingPreview';

Modal.setAppElement('#root');

const getJobStatus = (job) => {
  if (!job.applyByDate) return 'Open';
  const applyDateTimeString = `${job.applyByDate}T${job.applyByTime || '23:59:59'}`;
  const applyDateTime = new Date(applyDateTimeString);
  const now = new Date();
  return now < applyDateTime ? 'Open' : 'Closed';
};

const ITEMS_PER_PAGE = 10;

const AdminJobList = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [filterRole, setFilterRole] = useState('All');
  const [filterStatus, setFilterStatus] = useState('Open');
  const [filterCountry, setFilterCountry] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const fetchCountryForJobs = useCallback(async (jobsList) => {
    const uniqueLocations = [...new Set(jobsList.map(j => j.location).filter(l => l))];
    const locationMap = {};
    const promises = uniqueLocations.map(async (loc) => {
      try {
        const res = await axios.get(
          `https://nominatim.openstreetmap.org/search?q=${loc}&format=json&addressdetails=1&limit=1`
        );
        locationMap[loc] = res.data?.[0]?.address?.country || 'Unknown';
      } catch {
        locationMap[loc] = 'N/A';
      }
    });
    await Promise.all(promises);
    setJobs(jobsList.map(job => ({
      ...job,
      derivedCountry: locationMap[job.location] || 'N/A',
    })));
  }, []);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) { alert('Session expired.'); navigate('/'); return; }
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/jobs`,
        { headers: { Authorization: token } }
      );
      const initialJobs = res.data.map(job => ({
        ...job,
        status: getJobStatus(job),
        derivedCountry: 'Locating...',
      }));
      setJobs(initialJobs);
      setLoading(false);
      fetchCountryForJobs(initialJobs);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        alert('Session expired.'); localStorage.removeItem('adminToken'); navigate('/');
      }
      setLoading(false);
    }
  }, [fetchCountryForJobs, navigate]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);
  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterRole, filterStatus, filterCountry]);

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this job posting?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/job-postings/${id}`,
        { headers: { Authorization: token } }
      );
      setJobs(prev => prev.filter(j => j._id !== id));
      if (selectedJob?._id === id) closeModal();
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        alert('Session expired.'); navigate('/');
      } else alert('Failed to delete job.');
    }
  };

  const openModal = (job) => { setSelectedJob(job); setModalIsOpen(true); };
  const closeModal = () => { setModalIsOpen(false); setSelectedJob(null); };

  // Filtering
  const filteredJobs = jobs.filter(job => {
    if (filterRole !== 'All' && job.posterRole !== filterRole) return false;
    if (job.status !== filterStatus) return false;
    if (filterCountry !== 'All' && !(job.derivedCountry || '').includes(filterCountry)) return false;
    const term = searchTerm.toLowerCase();
    if (term && !(job.companyName || '').toLowerCase().includes(term) && !(job.role || '').toLowerCase().includes(term)) return false;
    return true;
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedJobs = filteredJobs.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);
  const goToPage = (p) => { if (p >= 1 && p <= totalPages) setCurrentPage(p); };

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (safePage <= 4) {
      pages.push(1, 2, 3, 4, 5, '…', totalPages);
    } else if (safePage >= totalPages - 3) {
      pages.push(1, '…', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '…', safePage - 1, safePage, safePage + 1, '…', totalPages);
    }
    return pages;
  };

  const uniqueCountries = [...new Set(
    jobs.map(j => j.derivedCountry).filter(c => c && c !== 'Locating...' && c !== 'N/A')
  )].sort();

  // Stats
  const openCount = jobs.filter(j => j.status === 'Open').length;
  const closedCount = jobs.filter(j => j.status === 'Closed').length;

  return (
    <div className="ajl-wrapper">

      {/* ═══ HEADER ═══ */}
      <div className="ajl-page-header">
        <div className="ajl-header-left">
          <div className="ajl-header-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
            </svg>
          </div>
          <div>
            <h1 className="ajl-title">Job Postings</h1>
            <p className="ajl-subtitle">{filteredJobs.length} result{filteredJobs.length !== 1 ? 's' : ''} • {openCount} open, {closedCount} closed</p>
          </div>
        </div>
        <div className="ajl-search-wrap">
          <svg className="ajl-search-svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input className="ajl-search" type="text" placeholder="Search company or role…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          {searchTerm && (
            <button className="ajl-search-x" onClick={() => setSearchTerm('')}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          )}
        </div>
      </div>

      {/* ═══ FILTER BAR ═══ */}
      <div className="ajl-filters">
        <div className="ajl-tabs">
          {[['All', 'All Roles'], ['Recruiter', 'Recruiters'], ['Customer', 'Customers']].map(([val, label]) => (
            <button key={val} className={`ajl-tab ${filterRole === val ? 'ajl-tab--on' : ''}`} onClick={() => setFilterRole(val)}>{label}</button>
          ))}
        </div>
        <div className="ajl-filters-right">
          <div className="ajl-dropdown-wrap">
            <svg className="ajl-dropdown-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
            <select className="ajl-dropdown" value={filterCountry} onChange={e => setFilterCountry(e.target.value)}>
              <option value="All">All Countries</option>
              {uniqueCountries.map((c, i) => <option key={i} value={c}>{c}</option>)}
            </select>
            <svg className="ajl-dropdown-chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
          </div>
          <div className="ajl-toggle">
            <button className={`ajl-toggle-btn ${filterStatus === 'Open' ? 'ajl-toggle--open' : ''}`} onClick={() => setFilterStatus('Open')}>
              <span className="ajl-dot ajl-dot--green" /> Open
            </button>
            <button className={`ajl-toggle-btn ${filterStatus === 'Closed' ? 'ajl-toggle--closed' : ''}`} onClick={() => setFilterStatus('Closed')}>
              <span className="ajl-dot ajl-dot--red" /> Closed
            </button>
          </div>
        </div>
      </div>

      {/* ═══ TABLE CARD ═══ */}
      <div className="ajl-card">
        {loading ? (
          <div className="ajl-loader"><div className="ajl-spin" /><p>Loading jobs…</p></div>
        ) : (
          <>
            <div className="ajl-scroll">
              <table className="ajl-table">
                <thead>
                  <tr>
                    <th className="ajl-th-num">#</th>
                    <th>Company</th>
                    <th>Job Title</th>
                    <th>Country</th>
                    <th>Type</th>
                    {/* --- SPLIT INTO THREE COLUMNS --- */}
                    <th>Poster Role</th>
                    <th>Poster Name</th>
                    <th>Poster Email</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th className="ajl-th-act">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedJobs.length > 0 ? paginatedJobs.map((job, idx) => (
                    <tr key={job._id}>
                      <td className="ajl-num">{(safePage - 1) * ITEMS_PER_PAGE + idx + 1}</td>
                      <td className="ajl-company">{job.companyName}</td>
                      <td className="ajl-jobtitle">{job.role}</td>
                      <td><span className="ajl-pill ajl-pill--country">{job.derivedCountry}</span></td>
                      <td className="ajl-type">{job.typeOfWork || 'Full-time'}</td>
                      
                      {/* --- THE THREE NEW CELLS --- */}
                      <td>
                        <span className={`ajl-poster-tag ajl-poster-tag--${(job.posterRole || 'recruiter').toLowerCase()}`}>
                          {job.posterRole || 'Recruiter'}
                        </span>
                      </td>
                      <td style={{ fontWeight: '500', color: '#e2e8f0' }}>
                        {job.postedByName || 'Unknown'}
                      </td>
                      <td style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                        {job.postedByEmail || '—'}
                      </td>
                      {/* --------------------------- */}

                      <td className="ajl-date">{new Date(job.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      <td><span className={`ajl-pill ajl-pill--${job.status.toLowerCase()}`}>{job.status}</span></td>
                      <td>
                        <div className="ajl-acts">
                          <button className="ajl-act ajl-act--view" onClick={() => openModal(job)}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                            View
                          </button>
                          <button className="ajl-act ajl-act--del" onClick={() => handleDelete(job._id)}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      {/* Updated colSpan from 9 to 11 to match the new columns */}
                      <td colSpan="11" className="ajl-empty-td">
                        <div className="ajl-empty">
                          <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.25 }}><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>
                          <p>No jobs match your current filters.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="ajl-pagi">
                <span className="ajl-pagi-info">
                  Showing <strong>{(safePage - 1) * ITEMS_PER_PAGE + 1}</strong> – <strong>{Math.min(safePage * ITEMS_PER_PAGE, filteredJobs.length)}</strong> of <strong>{filteredJobs.length}</strong>
                </span>
                <div className="ajl-pagi-btns">
                  <button className="ajl-pg" disabled={safePage === 1} onClick={() => goToPage(safePage - 1)} aria-label="Prev">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                  </button>
                  {getPageNumbers().map((p, i) =>
                    p === '…' ? <span key={`e${i}`} className="ajl-pg-dots">…</span> :
                      <button key={p} className={`ajl-pg ${safePage === p ? 'ajl-pg--on' : ''}`} onClick={() => goToPage(p)}>{p}</button>
                  )}
                  <button className="ajl-pg" disabled={safePage === totalPages} onClick={() => goToPage(safePage + 1)} aria-label="Next">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══ JOB DETAIL MODAL ═══ */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Job Details"
        className="ajl-modal"
        overlayClassName="ajl-overlay"
        shouldCloseOnOverlayClick
      >
        {selectedJob && (
          <>
            <div className="ajl-m-head">
              <div className="ajl-m-head-left">
                <div className="ajl-m-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>
                </div>
                <div>
                  <h2 className="ajl-m-title">{selectedJob.companyName}</h2>
                  <p className="ajl-m-sub">{selectedJob.role} • {selectedJob.typeOfWork || 'Full-time'}</p>
                </div>
              </div>
              <button className="ajl-m-close" onClick={closeModal} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="ajl-m-body">
              <JobPostingPreview formData={selectedJob} />
            </div>
            <div className="ajl-m-foot">
              <span className={`ajl-pill ajl-pill--${selectedJob.status.toLowerCase()}`} style={{ marginRight: 'auto' }}>
                {selectedJob.status}
              </span>
              <button className="ajl-act ajl-act--del" onClick={() => handleDelete(selectedJob._id)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                Delete
              </button>
              <button className="ajl-act ajl-act--close-gold" onClick={closeModal}>Close</button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default AdminJobList;